/**
 * 전체 MES 시스템 공통 계산 유틸리티
 * SAL1010, SAL2020, PUR1030, PUR2010 등에서 공통 사용
 */

/**
 * 기본 품목 금액 계산 (단가 × 수량 - 할인액)
 * @param qty 수량
 * @param price 단가
 * @param dc 할인금액 (기본값: 0)
 * @param preventNegative 음수 방지 여부 (기본값: true)
 * @returns 계산된 금액
 */
export const calculateItemAmount = (
  qty: number, 
  price: number, 
  dc: number = 0,
  preventNegative: boolean = true
): number => {
  const quantity = Number(qty) || 0;
  const unitPrice = Number(price) || 0;
  const discount = Number(dc) || 0;
  
  const calculatedAmount = quantity * unitPrice - discount;
  
  // 음수 방지 옵션
  return preventNegative ? Math.max(0, calculatedAmount) : calculatedAmount;
};


/**
 * 삭제된 항목들을 필터링
 * @param list 원본 리스트
 * @param deletedList 삭제된 항목의 식별자 리스트
 * @param idKey 식별자 필드명 (기본값: 'valueIndex')
 * @returns 삭제되지 않은 항목들
 */
export const filterDeletedItems = <T extends Record<string, any>>(
  list: T[], 
  deletedList: string[],
  idKey: string = 'valueIndex'
): T[] => {
  const deletedNumbers = deletedList.map(value => Number(value));
  return list.filter(item => !deletedNumbers.includes(Number(item[idKey])));
};



/**
 * 목록의 특정 필드 합계 계산
 * @param list 데이터 목록
 * @param fieldKey 합계할 필드명
 * @param deletedList 제외할 항목의 식별자 리스트 (옵션)
 * @param idKey 식별자 필드명 (기본값: 'valueIndex')
 * @returns 계산된 합계
 */
export const calculateSum = <T extends Record<string, any>>(
  list: T[],
  fieldKey: string,
  deletedList: string[] = [],
  idKey: string = 'valueIndex'
): number => {
  const activeList = filterDeletedItems(list, deletedList, idKey);
  return activeList.reduce((sum, item) => sum + (Number(item[fieldKey]) || 0), 0);
};


/**
 * 공통 상세 금액 계산 함수
 * @param detail 상세 데이터
 * @param fieldMapping 필드 매핑 설정
 * @returns 계산된 상세 데이터
 */
export const calculateDetailAmount = (
  detail: any,
  fieldMapping: {
    qtyField: string;
    priceField: string;
    dcField: string;
    amountField: string;
    preventNegative: boolean;
  }
) => {
  const qty = Number(detail[fieldMapping.qtyField]) || 0;
  const price = Number(detail[fieldMapping.priceField]) || 0;
  const dc = Number(detail[fieldMapping.dcField]) || 0;

  return {
    ...detail,
    [fieldMapping.amountField]: calculateItemAmount(qty, price, dc, fieldMapping.preventNegative)
  };
};
